import { useEffect, useLayoutEffect, useMemo } from "react";
import {
  ExceptionStatus,
  ListExceptionGroupsDocument,
  useLoadAssetLazyQuery,
} from "~/operations/types";
import { getTargetFromExceptionType } from "../lib/getTargetFromExceptionType";
import { ExceptionGroupRowProps } from "../components/ExceptionsGroupRow";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { RemoveExceptionsGroupDialogPayload } from "~/components/exceptions/RemoveExceptionsGroupDialog";

interface UseExceptionsGroupRowProps {
  exceptionGroup: ExceptionGroupRowProps["exceptionGroup"];
  isReviewLoading: ExceptionGroupRowProps["isReviewLoading"];
  loadingExceptionId: ExceptionGroupRowProps["loadingExceptionId"];
  isOpen: ExceptionGroupRowProps["isOpen"];
  isFocused: ExceptionGroupRowProps["isFocused"];
}

export default function useExceptionsGroupRow({
  exceptionGroup,
  isReviewLoading,
  loadingExceptionId,
  isOpen,
  isFocused,
}: UseExceptionsGroupRowProps) {
  // Hooks
  const { handleRemoveExceptionGroups } = useExceptions({
    scopeMrns: [exceptionGroup.scopeMrn],
    refetchQueries: [ListExceptionGroupsDocument],
  });

  const [loadAsset, { data: assetResult, loading: isAssetLoading }] =
    useLoadAssetLazyQuery({
      variables: { mrn: exceptionGroup.scopeMrn },
    });

  // values
  const exceptionGroupId = useMemo(
    () => exceptionGroup.exceptionId || exceptionGroup.id,
    [exceptionGroup.exceptionId, exceptionGroup.id],
  );
  const isAssetScope = exceptionGroup.scopeMrn.startsWith("//assets");
  const asset = assetResult?.asset;
  const scopeTag = exceptionGroup.title.split(":").pop();

  const target =
    exceptionGroup.exceptions.length > 0
      ? getTargetFromExceptionType(exceptionGroup.exceptions[0].__typename)
      : null;

  const isThisExceptionLoading = Boolean(
    isReviewLoading && loadingExceptionId === exceptionGroupId,
  );

  // effects
  useLayoutEffect(() => {
    if (isFocused && exceptionGroupId) {
      document
        .getElementById(exceptionGroupId)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isFocused, exceptionGroupId]);

  useEffect(() => {
    if (isAssetScope && isOpen && exceptionGroup.scopeMrn) {
      loadAsset({ variables: { mrn: exceptionGroup.scopeMrn } });
    }
  }, [isAssetScope, isOpen, exceptionGroup.scopeMrn, loadAsset]);

  // memoized objects
  const removeExceptionsGroupDialogPayload = useMemo(
    (): RemoveExceptionsGroupDialogPayload | undefined =>
      target || exceptionGroup.status === ExceptionStatus.New
        ? {
            onRemove: handleRemoveExceptionGroups,
            exceptionMrns: [exceptionGroup.mrn],
          }
        : undefined,
    [
      handleRemoveExceptionGroups,
      target,
      exceptionGroup.mrn,
      exceptionGroup.status,
    ],
  );

  return {
    asset,
    exceptionGroupId,
    isThisExceptionLoading,
    isAssetScope,
    isAssetLoading,
    scopeTag,
    removeExceptionsGroupDialogPayload,
  };
}
